import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { CloseButton, Loader, NoItemsYet } from 'components';
import { FindError, FindLoad, FindSuccessItem, Images, renderClientName } from 'utils';
import { appointmentActions, formActions, noteActions } from 'store';
import { AbcFormBuilder } from 'pages/forms/abcFormBuilder';
import { formBuilderStatusEnums, getServiceTemplate } from 'pages/forms/constants';
import { CreateAppSessionNote } from '../../../schedule/core/modals/apptDetail/createAppSessionNote';
import { noteEnums } from '../../../schedule/core/modals/apptDetail/constants';
import { DocumentationHistory } from './documentationHistory';
import { UnsavedChangesPrompt } from './unsavedChangesPrompt';
import { buildEntryNumbers, docEmptyState, docTabEnums, docTabFormModel, docTabs } from './constants';

const APPT_ON_MODEL = 'Appt';
const GET_NOTES_ACTION_TYPE = 'GET_GLOBAL_NOTES';
const SAVE_ACTION_TYPE = 'ADD_EDIT_APPT_NOTE';
const NOTES_REQUEST_PARAMS = { limit: 100 };
const OVERLAY_TRANSITION = { type: 'spring', damping: 28, stiffness: 240 };

export const SessionDocumentation = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const sessionById = useSelector((state) => state.session.sessionById);
    const appt = useSelector((state) => state.appointment.appointmentById);
    const allNotes = useSelector((state) => state.note?.notes?.data);
    const apptId = appt?.id || sessionById?.appt?.id;
    const loadNotes = FindLoad(GET_NOTES_ACTION_TYPE);
    const saveSuccess = FindSuccessItem(SAVE_ACTION_TYPE);
    const saveError = FindError(SAVE_ACTION_TYPE);
    const [activeTab, setActiveTab] = useState(docTabEnums.ABC_DATA);
    const [selectedId, setSelectedId] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [formSeed, setFormSeed] = useState(0);
    const [pendingExit, setPendingExit] = useState(null);
    const [exitSaving, setExitSaving] = useState(false);
    const formRef = useRef(null);
    const autoSelectRef = useRef(true);
    const awaitingNewestRef = useRef(false);
    const afterSaveRef = useRef(null);

    const getNotes = useCallback(
        (load) => {
            if (apptId) dispatch(noteActions.getGlobalNotes(apptId, APPT_ON_MODEL, NOTES_REQUEST_PARAMS, load));
        },
        [apptId, dispatch]
    );

    useEffect(() => {
        if (open) getNotes();
    }, [open, getNotes]);

    useEffect(() => {
        if (!open) return;
        setActiveTab(docTabEnums.ABC_DATA);
        setSelectedId(null);
        setIsCreating(false);
        autoSelectRef.current = true;
        afterSaveRef.current = null;
        awaitingNewestRef.current = false;
        setPendingExit(null);
        setExitSaving(false);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleBeforeUnload = (event) => {
            if (!formRef.current?.isDirty?.()) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [open]);

    const entriesByTab = useMemo(() => {
        const byType = { [docTabEnums.ABC_DATA]: [], [docTabEnums.SESSION_NOTE]: [] };
        for (const note of allNotes || []) {
            if (note?.type === noteEnums.ABC_DATA) byType[docTabEnums.ABC_DATA].push(note);
            else if (note?.type === noteEnums.CLINICAL_NOTES) byType[docTabEnums.SESSION_NOTE].push(note);
        }
        // Newest first, matching the design.
        Object.values(byType).forEach((list) => list.sort((a, b) => new Date(b?.created) - new Date(a?.created)));
        return byType;
    }, [allNotes]);

    const entries = entriesByTab[activeTab];
    const entryNumbers = useMemo(() => buildEntryNumbers(entries), [entries]);
    const selectedEntry = useMemo(() => entries?.find((entry) => entry?.id === selectedId) || null, [entries, selectedId]);

    // Default view per tab: the most recent entry, read/edit — not a blank form.
    useEffect(() => {
        if (!open || !autoSelectRef.current || !entries?.length) return;
        autoSelectRef.current = false;
        setSelectedId(entries[0]?.id || null);
    }, [open, entries]);

    useEffect(() => {
        if (!saveSuccess) return;
        const afterSave = afterSaveRef.current;
        // On create there is no id to re-select, so remember to jump to the newest entry of this
        // tab once the refreshed list lands. Skipped when the save came from the unsaved-changes
        // prompt, whose exit decides where the clinician goes next.
        if (isCreating && !afterSave) awaitingNewestRef.current = true;
        getNotes('noLoad');
        if (afterSave) {
            afterSaveRef.current = null;
            setExitSaving(false);
            setPendingExit(null);
            afterSave();
        }
    }, [saveSuccess]);

    // If the prompt's save fails, drop the pending exit — the clinician stays on the form, which
    // shows the error, instead of waiting on an exit that will never come.
    useEffect(() => {
        if (!saveError || !afterSaveRef.current) return;
        afterSaveRef.current = null;
        setExitSaving(false);
        setPendingExit(null);
    }, [saveError]);

    // A create just landed — switch out of creation mode onto the entry it produced.
    useEffect(() => {
        if (!awaitingNewestRef.current || !entries?.length) return;
        awaitingNewestRef.current = false;
        setIsCreating(false);
        setSelectedId(entries[0]?.id || null);
    }, [entries]);

    /**
     * Templates for the active tab. Both builders read the published list off
     * `state.forms.forms`, so the fetch belongs to the host — exactly like the appt Notes tab
     * and Client Documentation do it — not to the builders, which remount on every selection.
     *
     * ABC Data has at most one published template per org (enforced in `FormBuilderService`),
     * so the ABC tab's creation flow just takes the first one, with no picker.
     */
    useEffect(() => {
        if (!open) return;
        dispatch(formActions.resetForms());
        dispatch(formActions.getForms({ model: docTabFormModel[activeTab], status: formBuilderStatusEnums.PUBLISHED }));
    }, [open, activeTab, dispatch]);

    /**
     * Every way out of the open form (close, backdrop, tab switch, another entry, Add new,
     * Cancel) goes through here. With unsaved edits the clinician picks Save / Continue without
     * saving / Cancel instead of losing them. Whether Save is offered depends on note-type rules
     * the form owns (no exit may move a note through the review workflow), so it asks
     * `canAutoSave`.
     */
    const guardExit = (action) => {
        const form = formRef.current;
        if (!form?.isDirty?.()) {
            action();
            return;
        }
        setPendingExit({ action, canSave: !!form.canAutoSave?.() });
    };

    const handleExitSave = () => {
        if (!pendingExit) return;
        afterSaveRef.current = pendingExit.action;
        setExitSaving(true);
        formRef.current?.submitDraft();
    };

    const handleExitDiscard = () => {
        const action = pendingExit?.action;
        setPendingExit(null);
        action?.();
    };

    const handleExitCancel = useCallback(() => setPendingExit(null), []);

    const handleSelectTab = (tabId) => {
        if (tabId === activeTab) return;
        guardExit(() => {
            setActiveTab(tabId);
            setSelectedId(null);
            setIsCreating(false);
            autoSelectRef.current = true;
        });
    };

    const handleSelectEntry = (entry) => {
        if (!entry?.id || (entry.id === selectedId && !isCreating)) return;
        guardExit(() => {
            autoSelectRef.current = false;
            setIsCreating(false);
            setSelectedId(entry.id);
        });
    };

    const handleAddNew = () => {
        // Already on a blank new entry: nothing changes, so nothing to warn about.
        if (isCreating) return;
        guardExit(() => {
            autoSelectRef.current = false;
            setSelectedId(null);
            setIsCreating(true);
        });
    };

    /**
     * Cancel inside an inline form. There is no modal of its own to close, so it drops the
     * creation flow (back to the default view) or, when editing, discards unsaved edits by
     * remounting the builder on the entry's stored values.
     */
    const handleCancelForm = () => {
        guardExit(() => {
            if (isCreating) {
                setIsCreating(false);
                setSelectedId(entries?.[0]?.id || null);
            }
            setFormSeed((seed) => seed + 1);
        });
    };

    const handleClose = () => guardExit(onClose);

    const renderBanner = () => {
        if (!isCreating && !selectedEntry) return null;
        if (selectedEntry) {
            const number = entryNumbers?.[selectedEntry.id];
            return (
                <div className="sdoc-banner sdoc-banner-editing">
                    <img src={Images.editPenUnderline} alt="edit" />
                    <p>
                        You are  <b>editing {selectedEntry?.template?.title}</b>. Saving will update this existing entry.
                    </p>
                </div>
            );
        }
        return (
            <div className="sdoc-banner sdoc-banner-creating">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <g clip-path="url(#clip0_34256_208932)">
                        <path d="M7.34457 1.87253C7.40352 1.55692 7.67901 1.32812 8.00008 1.32812C8.32115 1.32812 8.59663 1.55692 8.65558 1.87253L9.35643 5.57883C9.4583 6.11809 9.88011 6.5399 10.4194 6.64177L14.1257 7.34262C14.4413 7.40157 14.6701 7.67706 14.6701 7.99813C14.6701 8.31919 14.4413 8.59468 14.1257 8.65363L10.4194 9.35448C9.88011 9.45635 9.4583 9.87816 9.35643 10.4174L8.65558 14.1237C8.59663 14.4393 8.32115 14.6681 8.00008 14.6681C7.67901 14.6681 7.40352 14.4393 7.34457 14.1237L6.64372 10.4174C6.54186 9.87816 6.12005 9.45635 5.58078 9.35448L1.87448 8.65363C1.55887 8.59468 1.33008 8.31919 1.33008 7.99813C1.33008 7.67706 1.55887 7.40157 1.87448 7.34262L5.58078 6.64177C6.12005 6.5399 6.54186 6.11809 6.64372 5.57883L7.34457 1.87253Z" stroke="#0B5DCF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M13.3301 1.32812V3.99813" stroke="#0B5DCF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M14.67 2.67188H12" stroke="#0B5DCF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M1.32999 13.335C1.32999 12.5977 1.92769 12 2.66499 12C3.40229 12 3.99999 12.5977 3.99999 13.335C3.99999 14.0723 3.40229 14.67 2.66499 14.67C1.92769 14.67 1.32999 14.0723 1.32999 13.335Z" stroke="#0B5DCF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_34256_208932">
                            <rect width="16" height="16" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
                <p>
                    You are <b>creating a new entry</b>. Saving will add a new item to the history.
                </p>
            </div>
        );
    };

    // Session notes keep the service-driven template rules they have everywhere else.
    const serviceTemplate = getServiceTemplate(appt?.authorizedService?.service);

    const renderContent = () => {
        // Nothing selected and nothing being created: this tab is empty and the clinician has
        // not asked for a form yet, so offer the entry point instead of a blank template.
        if (!isCreating && !selectedEntry) {
            if (loadNotes?.length) return <Loader height={'320px'} />;
            const empty = docEmptyState[activeTab];
            return (
                <NoItemsYet
                    height={'320px'}
                    image={empty.image}
                    text={empty.title}
                    subTitle={empty.subTitle}
                    btnText={'Add new entry'}
                    handleAdd={handleAddNew}
                    containerStyles={{ position: 'unset' }}
                />
            );
        }

        // Keying on the entry id remounts the builder when the selection changes, so the form
        // resets cleanly instead of leaking the previous entry's values.
        const key = `${activeTab}-${selectedId || 'new'}-${formSeed}`;

        if (activeTab === docTabEnums.ABC_DATA) {
            return (
                <AbcFormBuilder
                    key={key}
                    ref={formRef}
                    info={selectedEntry}
                    resourceId={apptId}
                    ACTION_TYPE={SAVE_ACTION_TYPE}
                    handleSaveNote={(data) => {
                        const noteData = { ...data, resource: apptId, type: noteEnums.ABC_DATA, status: data?.status };
                        delete noteData?.id;
                        if (selectedEntry?.id) noteData.id = selectedEntry.id;
                        dispatch(appointmentActions.addEditApptNote(apptId, noteData));
                    }}
                />
            );
        }

        return (
            <CreateAppSessionNote
                key={key}
                ref={formRef}
                width={'100%'}
                appt={appt}
                info={selectedEntry}
                noteType={noteEnums.CLINICAL_NOTES}
                serviceTemplate={serviceTemplate}
                onCancel={handleCancelForm}
            />
        );
    };

    return (
        <AnimatePresence>
            {open && (
                <div className="sdoc-root">
                    <motion.div
                        className="sdoc-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                    />
                    <motion.div
                        className="sdoc-modal"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 16 }}
                        transition={OVERLAY_TRANSITION}
                    >
                        <div className="sdoc-header">
                            <h3>Documentation</h3>
                            <CloseButton handleCLic={handleClose} />
                        </div>

                        <div className="sdoc-body">
                            <DocumentationHistory
                                clientName={renderClientName(sessionById?.appt?.client)}
                                entries={entries}
                                entryNumbers={entryNumbers}
                                activeTab={activeTab}
                                selectedId={selectedId}
                                isCreating={isCreating}
                                onSelect={handleSelectEntry}
                                onAddNew={handleAddNew}
                                loading={!!loadNotes?.length}
                            />

                            <div className="sdoc-content">
                                {renderBanner()}

                                <div className="sdoc-tabs">
                                    {docTabs.map((tab) => (
                                        <button
                                            type="button"
                                            key={tab.id}
                                            onClick={() => handleSelectTab(tab.id)}
                                            className={`sdoc-tab ${activeTab === tab.id ? 'sdoc-tab-active' : ''}`}
                                            style={activeTab === tab.id ?
                                                { color: '#2563EB', filter: 'brightness(0) saturate(100%) invert(30%) sepia(50%) saturate(4206%) hue-rotate(216deg) brightness(95%) contrast(94%)'}
                                                :
                                                { color: '#6B7280', filter: 'brightness(0) saturate(100%) invert(51%) sepia(8%) saturate(649%) hue-rotate(182deg) brightness(84%) contrast(86%)'}
                                        }
                                        >
                                            <img src={tab.icon} alt={tab.label} />
                                            <span>{tab.label}</span>
                                        </button>
                                    ))}
                                </div>

                                <div className="sdoc-form">{renderContent()}</div>
                            </div>
                        </div>
                    </motion.div>

                    <UnsavedChangesPrompt
                        open={!!pendingExit}
                        canSave={!!pendingExit?.canSave}
                        saving={exitSaving}
                        onSave={handleExitSave}
                        onDiscard={handleExitDiscard}
                        onCancel={handleExitCancel}
                    />
                </div>
            )}
        </AnimatePresence>
    );
};
